<template>
    <v-dialog v-model="show" fullscreen persistent transition="dialog-bottom-transition">
        <v-card class="fullscreen-card">
            <v-app-bar flat class="modal-header">
                <v-icon color="primary" class="mr-3">mdi-package-variant</v-icon>
                <v-toolbar-title class="modal-title">Paket Aufteilen</v-toolbar-title>
                <v-spacer></v-spacer>
                <div class="progress-indicator">
                    <v-chip color="primary" outlined class="mr-4">
                        <v-icon left small>mdi-check-circle</v-icon>
                        {{ assignedArticlesCount }}/{{ totalArticles }} zugewiesen
                    </v-chip>
                    <v-chip color="warning" outlined class="mr-4">
                        <v-icon left small>mdi-scale</v-icon>
                        {{ packagingsWithWeight }}/{{ packagings.length }} Gewichte
                    </v-chip>
                </div>
                <v-btn icon large @click="$emit('close')">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-app-bar>

            <div class="fullscreen-content d-flex">
                <!-- Left Sidebar: Available Articles -->
                <div class="articles-sidebar">
                    <div class="sidebar-header">
                        <h3 class="sidebar-title">
                            <v-icon color="primary" class="mr-2">mdi-cube-outline</v-icon>
                            Verfügbare Artikel
                        </h3>
                        <v-chip color="primary" small>
                            {{ unassignedArticles.length }} verbleibend
                        </v-chip>
                    </div>
                    
                    <div class="sidebar-content">
                        <!-- Articles Grid -->
                        <div class="articles-grid">
                            <div
                                v-for="article in articles"
                                :key="article.designId"
                                :class="[
                                    'article-card',
                                    { 
                                        'assigned': isArticleAssigned(article),
                                        'dragging': isDragging && draggedArticle?.designId === article.designId
                                    }
                                ]"
                                :draggable="!isArticleAssigned(article)"
                                @dragstart="startDrag(article, $event)"
                                @dragend="endDrag"
                            >
                                <div class="article-content">
                                    <div class="article-icon">
                                        <v-icon 
                                            :color="isArticleAssigned(article) ? 'success' : 'primary'"
                                            size="20"
                                        >
                                            mdi-drag
                                        </v-icon>
                                    </div>
                                    <div class="article-info">
                                        <div class="article-sku">{{ article.sku }}</div>
                                        <div class="article-meta">
                                            <span v-if="isArticleAssigned(article)" class="assigned-to">
                                                → Paket {{ getArticlePackageIndex(article) + 1 }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Drag handle -->
                                <div 
                                    v-if="!isArticleAssigned(article)" 
                                    class="drag-handle"
                                >
                                    <v-icon color="grey lighten-1" size="16">mdi-cursor-move</v-icon>
                                </div>
                            </div>
                        </div>
                        
                    </div>
                </div>

                <!-- Main Content Area: Packages -->
                <div class="packages-main-area">
                    <div class="main-area-header">
                        <h3 class="main-title">
                            <v-icon color="primary" class="mr-2">mdi-package-variant</v-icon>
                            Verpackungen ({{ packagings.length }})
                        </h3>
                        <v-btn 
                            @click="addNewPackaging" 
                            color="primary" 
                            outlined
                            class="add-package-btn"
                        >
                            <v-icon left>mdi-plus</v-icon>
                            Neue Verpackung
                        </v-btn>
                    </div>

                    <!-- Packaging Grid -->
                    <div class="packages-grid">
                        <div
                            v-for="(packaging, index) in packagings"
                            :key="packaging.id"
                            :class="[
                                'package-card',
                                { 
                                    'drop-active': isDragging,
                                    'drop-target': isDragging && dropTargetIndex === index
                                }
                            ]"
                            @drop.stop="dropArticle($event, index)"
                            @dragover.prevent.stop="handleDragOver(index)"
                            @dragenter.prevent.stop="handleDragEnter(index)"
                            @dragleave.prevent.stop="handleDragLeave(index)"
                        >
                            <v-card outlined class="packaging-card h-100">
                            <!-- Dynamic Packaging Header -->
                            <v-card-title class="packaging-header">
                                <div class="header-content">
                                    <!-- No Packaging State -->
                                    <div v-if="!packaging.recommendedCarton && !packaging.isLoadingCarton && (packaging.articles?.length || 0) === 0" class="no-packaging-state">
                                        <v-icon color="grey" class="mr-2">mdi-package-variant-closed</v-icon>
                                        <span>Verpackung {{ index + 1 }}</span>
                                        <small class="text-caption grey--text d-block">Keine Artikel zugewiesen</small>
                                    </div>
                                    
                                    <!-- Loading State -->
                                    <div v-else-if="packaging.isLoadingCarton" class="loading-packaging-state">
                                        <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2"></v-progress-circular>
                                        <span>Karton wird berechnet...</span>
                                        <small class="text-caption d-block">{{ packaging.articles?.length || 0 }} Artikel</small>
                                    </div>
                                    
                                    <!-- Selected Packaging State -->
                                    <div v-else-if="packaging.recommendedCarton" class="selected-packaging-state">
                                        <div class="carton-image-container">
                                            <v-img
                                                v-if="packaging.recommendedCarton.img"
                                                :src="packaging.recommendedCarton.img"
                                                height="44"
                                                width="44"
                                                contain
                                                class="carton-img"
                                            ></v-img>
                                            <v-icon v-else color="brown" size="28">mdi-package-variant</v-icon>
                                        </div>
                                        <div class="carton-details">
                                            <div class="carton-name">{{ packaging.recommendedCarton.title || packaging.recommendedCarton.sku }}</div>
                                            <div class="carton-specs">
                                                <span class="carton-sku">{{ packaging.recommendedCarton.sku }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Error State -->
                                    <div v-else-if="packaging.packingError" class="error-packaging-state">
                                        <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                                        <div>
                                            <div>Kein Karton gefunden</div>
                                            <small class="text-caption error--text">{{ packaging.packingError }}</small>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Remove Button -->
                                <v-btn
                                    icon
                                    small
                                    color="error"
                                    @click="removePackaging(index)"
                                    :disabled="packagings.length <= 2"
                                >
                                    <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-card-text>
                                <!-- Assigned Articles -->
                                <div class="articles-section">
                                    <h4 class="mb-3">
                                        Zugewiesene Artikel ({{ packaging.articles?.length || 0 }})
                                    </h4>
                                    
                                    <div v-if="(packaging.articles?.length || 0) === 0" class="empty-drop-zone">
                                        <v-icon 
                                            :color="isDragging && dropTargetIndex === index ? 'primary' : 'grey'" 
                                            size="48"
                                        >
                                            {{ isDragging && dropTargetIndex === index ? 'mdi-package-down' : 'mdi-package-variant-closed' }}
                                        </v-icon>
                                        <p 
                                            :class="[
                                                'text-caption mt-2',
                                                isDragging && dropTargetIndex === index ? 'primary--text' : 'grey--text'
                                            ]"
                                        >
                                            {{ isDragging && dropTargetIndex === index ? 'Artikel hier ablegen' : 'Artikel hier zuweisen' }}
                                        </p>
                                        <div v-if="isDragging && dropTargetIndex === index" class="drop-hint">
                                            <v-icon color="primary" size="20">mdi-arrow-down</v-icon>
                                            <p v-if="dragSourcePackageIndex !== null" class="text-caption primary--text">
                                                Von Paket {{ dragSourcePackageIndex + 1 }} verschieben
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div 
                                        v-else 
                                        class="assigned-articles drop-zone"
                                    >
                                        <!-- Drop indicator for inter-package moves -->
                                        <div 
                                            v-if="isDragging && dropTargetIndex === index && dragSourcePackageIndex !== null && dragSourcePackageIndex !== index" 
                                            class="inter-package-drop-indicator"
                                        >
                                            <v-icon color="primary">mdi-swap-horizontal</v-icon>
                                            <span>Von Paket {{ dragSourcePackageIndex + 1 }} hierher verschieben</span>
                                        </div>
                                        
                                        <div 
                                            v-for="article in packaging.articles"
                                            :key="article.designId"
                                            :class="[
                                                'assigned-article',
                                                { 'dragging': isDragging && draggedArticle?.designId === article.designId }
                                            ]"
                                            draggable
                                            @dragstart="startDragFromPackage(article, index, $event)"
                                            @dragend="endDrag"
                                        >
                                            <div class="drag-handle-assigned">
                                                <v-icon size="16" color="grey lighten-1">mdi-drag</v-icon>
                                            </div>
                                            <div class="article-info">
                                                <div class="article-main">
                                                    <span class="article-sku">{{ article.sku }}</span>
                                                    <span v-if="article.qty >= 1" class="article-qty">{{ article.qty }}x</span>
                                                </div>
                                                <span v-if="article.scanned" class="scan-status">
                                                    <v-icon small color="success">mdi-check</v-icon>
                                                </span>
                                            </div>
                                            <div class="article-actions">
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn
                                                            icon
                                                            x-small
                                                            color="error"
                                                            @click="removeArticleFromPackaging(index, article)"
                                                            v-bind="attrs"
                                                            v-on="on"
                                                        >
                                                            <v-icon x-small>mdi-close</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Artikel entfernen</span>
                                                </v-tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Weight Input -->
                                <div class="weight-section">
                                    <v-text-field
                                        v-model.number="packaging.weight"
                                        label="Gewicht (g)"
                                        type="number"
                                        outlined
                                        dense
                                        :rules="[rules.required, rules.minWeight]"
                                        :disabled="(packaging.articles?.length || 0) === 0"
                                        @input="validateWeight(index)"
                                        class="weight-input"
                                    >
                                        <template v-slot:append>
                                            <span class="text-caption">g</span>
                                        </template>
                                    </v-text-field>
                                </div>

                            </v-card-text>
                        </v-card>
                        </div>
                    </div>
                </div>
            </div>

            <v-divider></v-divider>

            <v-card-actions class="pa-4">
                <div class="flex-grow-1">
                    <div class="split-summary">
                        <div class="summary-item">
                            <v-icon color="primary" class="mr-1">mdi-cube-outline</v-icon>
                            {{ packagings.length }} Verpackungen
                        </div>
                        <div class="summary-item">
                            <v-icon color="success" class="mr-1">mdi-check</v-icon>
                            {{ assignedArticlesCount }}/{{ totalArticles }} Artikel zugewiesen
                        </div>
                        <div class="summary-item">
                            <v-icon color="warning" class="mr-1">mdi-scale</v-icon>
                            {{ packagingsWithWeight }}/{{ packagings.length }} Gewichte gesetzt
                        </div>
                    </div>
                </div>
                
                <v-spacer></v-spacer>
                
                <v-btn text @click="$emit('close')">
                    Abbrechen
                </v-btn>
                <v-btn
                    :disabled="!canCompleteSplit"
                    :loading="isProcessing"
                    @click="finalizeSplit"
                    color="primary"
                >
                    Split abschließen
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import PacklistService from '@/services/PacklistService';
import CartonItem from '../../shared/items/CartonItem.vue';

export default {
    name: 'PackagingSplitModal',
    components: {
        CartonItem
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        articles: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            packagings: [
                {
                    id: 1,
                    articles: [],
                    weight: null,
                    recommendedCarton: null,
                    isLoadingCarton: false,
                    packingError: null,
                    utilizationPercent: null
                },
                {
                    id: 2,
                    articles: [],
                    weight: null,
                    recommendedCarton: null,
                    isLoadingCarton: false,
                    packingError: null,
                    utilizationPercent: null
                }
            ],
            isProcessing: false,
            nextPackagingId: 3,
            // Drag and drop state
            isDragging: false,
            draggedArticle: null,
            dragSourcePackageIndex: null, // Track which package the article came from
            dropTargetIndex: null,
            dragLeaveTimeout: null, // Prevent flickering
            rules: {
                required: value => !!value || 'Gewicht ist erforderlich',
                minWeight: value => value > 0 || 'Gewicht muss größer als 0 sein'
            }
        }
    },
    computed: {
        totalArticles() {
            return this.articles?.length || 0;
        },
        assignedArticlesCount() {
            return this.packagings?.reduce((total, packaging) => total + (packaging.articles?.length || 0), 0) || 0;
        },
        unassignedArticles() {
            // Articles that haven't been assigned to any package yet
            if (!this.articles || !this.packagings) return [];
            
            return this.articles.filter(article => 
                !this.packagings.some(packaging => 
                    packaging.articles?.some(assigned => 
                        assigned.sku === article.sku && assigned.designId === article.designId
                    )
                )
            );
        },
        packagingsWithWeight() {
            if (!this.packagings) return 0;
            
            return this.packagings.filter(packaging => 
                (packaging.articles?.length || 0) === 0 || (packaging.weight && packaging.weight > 0)
            ).length;
        },
        canCompleteSplit() {
            if (!this.articles || !this.packagings) return false;
            
            // All articles must be assigned and all packages must have weight and carton
            const allArticlesAssigned = this.assignedArticlesCount >= this.totalArticles;
            const allHaveWeight = this.packagings.every(packaging => 
                (packaging.articles?.length || 0) === 0 || (packaging.weight && packaging.weight > 0)
            );
            const allHaveCartons = this.packagings.every(packaging => 
                (packaging.articles?.length || 0) === 0 || packaging.recommendedCarton
            );
            
            return allArticlesAssigned && allHaveWeight && allHaveCartons;
        }
    },
    watch: {
        show: {
            handler(newVal) {
                if (newVal) {
                    // Reset state when modal opens
                    this.resetModalState();
                }
            }
        }
    },
    methods: {
        resetModalState() {
            // Reset packagings to initial state
            this.packagings = [
                {
                    id: 1,
                    articles: [],
                    weight: null,
                    recommendedCarton: null,
                    isLoadingCarton: false,
                    packingError: null,
                    utilizationPercent: null
                },
                {
                    id: 2,
                    articles: [],
                    weight: null,
                    recommendedCarton: null,
                    isLoadingCarton: false,
                    packingError: null,
                    utilizationPercent: null
                }
            ];
            this.isProcessing = false;
            this.nextPackagingId = 3;
            // Reset drag state
            this.isDragging = false;
            this.draggedArticle = null;
            this.dragSourcePackageIndex = null;
            this.dropTargetIndex = null;
            
            // Clear any pending timeouts
            if (this.dragLeaveTimeout) {
                clearTimeout(this.dragLeaveTimeout);
                this.dragLeaveTimeout = null;
            }
        },

        // Helper methods for article assignment checking
        isArticleAssigned(article) {
            if (!this.packagings) return false;
            return this.packagings.some(packaging => 
                packaging.articles?.some(assigned => 
                    assigned.sku === article.sku && assigned.designId === article.designId
                )
            );
        },
        
        getArticlePackageIndex(article) {
            if (!this.packagings) return -1;
            return this.packagings.findIndex(packaging => 
                packaging.articles?.some(assigned => 
                    assigned.sku === article.sku && assigned.designId === article.designId
                )
            );
        },

        // Enhanced drag and drop assignment methods
        startDrag(article, event) {
            if (this.isArticleAssigned(article)) {
                event.preventDefault();
                return;
            }
            
            this.isDragging = true;
            this.draggedArticle = article;
            this.dragSourcePackageIndex = null; // From sidebar
            event.dataTransfer.setData('application/json', JSON.stringify({ 
                article: article, 
                sourcePackageIndex: null 
            }));
            event.dataTransfer.effectAllowed = 'move';
            
            // Add visual feedback to dragged element
            event.dataTransfer.setDragImage(event.target, event.target.offsetWidth / 2, event.target.offsetHeight / 2);
            
            console.log('🎯 Started dragging article from sidebar:', article.sku);
        },

        // Drag from assigned package (inter-package movement)
        startDragFromPackage(article, sourcePackageIndex, event) {
            this.isDragging = true;
            this.draggedArticle = article;
            this.dragSourcePackageIndex = sourcePackageIndex;
            event.dataTransfer.setData('application/json', JSON.stringify({ 
                article: article, 
                sourcePackageIndex: sourcePackageIndex 
            }));
            event.dataTransfer.effectAllowed = 'move';
            
            // Add visual feedback to dragged element
            event.dataTransfer.setDragImage(event.target, event.target.offsetWidth / 2, event.target.offsetHeight / 2);
            
            console.log(`🔄 Started dragging article between packages: ${article.sku} from package ${sourcePackageIndex + 1}`);
        },

        endDrag() {
            // Clear any pending timeouts to prevent flickering
            if (this.dragLeaveTimeout) {
                clearTimeout(this.dragLeaveTimeout);
                this.dragLeaveTimeout = null;
            }
            
            this.isDragging = false;
            this.draggedArticle = null;
            this.dragSourcePackageIndex = null;
            this.dropTargetIndex = null;
            console.log('🎯 Drag ended');
        },

        handleDragOver(index) {
            if (this.isDragging && this.dropTargetIndex !== index) {
                this.dropTargetIndex = index;
            }
        },

        handleDragEnter(index) {
            if (this.isDragging) {
                // Clear any pending leave timeout to prevent flickering
                if (this.dragLeaveTimeout) {
                    clearTimeout(this.dragLeaveTimeout);
                    this.dragLeaveTimeout = null;
                }
                
                if (this.dropTargetIndex !== index) {
                    this.dropTargetIndex = index;
                }
            }
        },

        handleDragLeave(index) {
            if (this.isDragging && this.dropTargetIndex === index) {
                // Clear any existing timeout
                if (this.dragLeaveTimeout) {
                    clearTimeout(this.dragLeaveTimeout);
                }
                
                // Set new timeout to clear drop target
                this.dragLeaveTimeout = setTimeout(() => {
                    if (this.dropTargetIndex === index && this.isDragging) {
                        this.dropTargetIndex = null;
                    }
                    this.dragLeaveTimeout = null;
                }, 100);
            }
        },

        async dropArticle(event, targetPackageIndex) {
            event.preventDefault();
            event.stopPropagation();
            
            // Clear any pending timeouts immediately
            if (this.dragLeaveTimeout) {
                clearTimeout(this.dragLeaveTimeout);
                this.dragLeaveTimeout = null;
            }
            
            this.dropTargetIndex = null;
            
            if (!this.isDragging) return;
            
            try {
                const dropData = JSON.parse(event.dataTransfer.getData('application/json'));
                const { article, sourcePackageIndex } = dropData;
                
                // Handle different drop scenarios
                if (sourcePackageIndex !== null) {
                    // Moving between packages
                    await this.moveArticleBetweenPackages(article, sourcePackageIndex, targetPackageIndex);
                } else {
                    // New assignment from sidebar
                    if (this.isArticleAssigned(article)) {
                        this.$emit('show-message', {
                            message: `❌ ${article.sku} bereits zugewiesen`,
                            color: 'warning'
                        });
                        return;
                    }
                    
                    await this.assignArticleToPackaging(article, targetPackageIndex);
                    
                    this.$emit('show-message', {
                        message: `📦 ${article.sku} zu Paket ${targetPackageIndex + 1} hinzugefügt`,
                        color: 'success'
                    });
                }
                
            } catch (error) {
                console.error('Error processing drop:', error);
                this.$emit('show-message', {
                    message: '❌ Fehler beim Zuweisen des Artikels',
                    color: 'error'
                });
            } finally {
                this.endDrag();
            }
        },

        // Move article between packages
        async moveArticleBetweenPackages(article, sourceIndex, targetIndex) {
            if (sourceIndex === targetIndex) {
                console.log('📦 Article dropped on same package, no action needed');
                return;
            }
            
            const sourcePackage = this.packagings[sourceIndex];
            const targetPackage = this.packagings[targetIndex];
            
            // Remove from source package
            const articleIndex = sourcePackage.articles.findIndex(a => 
                a.sku === article.sku && a.designId === article.designId
            );
            
            if (articleIndex >= 0) {
                sourcePackage.articles.splice(articleIndex, 1);
                console.log(`📤 Removed ${article.sku} from package ${sourceIndex + 1}`);
                
                // Recalculate source package carton
                await this.updatePackageCartonRecommendation(sourceIndex);
            }
            
            // Add to target package
            targetPackage.articles.push({ ...article });
            console.log(`📥 Added ${article.sku} to package ${targetIndex + 1}`);
            
            // Recalculate target package carton
            await this.updatePackageCartonRecommendation(targetIndex);
            
            this.$emit('show-message', {
                message: `🔄 ${article.sku} von Paket ${sourceIndex + 1} zu Paket ${targetIndex + 1} verschoben`,
                color: 'info'
            });
        },


        // Core assignment logic
        async assignArticleToPackaging(article, packagingIndex) {
            const packaging = this.packagings[packagingIndex];
            
            // Check if already assigned
            const alreadyAssigned = packaging.articles.some(assigned => 
                assigned.sku === article.sku && assigned.designId === article.designId
            );

            if (alreadyAssigned) {
                this.$emit('show-message', {
                    message: `❌ ${article.sku} bereits in Verpackung ${packagingIndex + 1}`,
                    color: 'warning'
                });
                return;
            }
            
            // Add article to packaging
            packaging.articles.push({ ...article });
            
            // Run smart packaging calculation for this package
            await this.updatePackageCartonRecommendation(packagingIndex);
            
            this.$emit('show-message', {
                message: `✅ ${article.sku} zu Verpackung ${packagingIndex + 1} hinzugefügt`,
                color: 'success'
            });
            
            console.log(`📦 Article ${article.sku} assigned to package ${packagingIndex + 1}`);
        },

        // Smart carton selection for a specific package
        async updatePackageCartonRecommendation(packagingIndex) {
            const packaging = this.packagings[packagingIndex];
            
            if (!packaging.articles || packaging.articles.length === 0) {
                packaging.recommendedCarton = null;
                packaging.packingError = null;
                packaging.utilizationPercent = null;
                return;
            }
            
            try {
                packaging.isLoadingCarton = true;
                packaging.packingError = null;
                
                // Extract SKUs from assigned articles
                const skus = packaging.articles.map(article => article.sku);
                
                console.log(`🤖 Running smart packaging for package ${packagingIndex + 1}:`, skus);
                
                // Call the smart packaging selection API
                const response = await PacklistService.selectPackagingBySkus(skus);
                
                if (response.status === 200 && response.data.success) {
                    packaging.recommendedCarton = response.data.selectedCarton;
                    packaging.utilizationPercent = Math.round(response.data.utilization.volumeRatio * 100);
                    packaging.packingError = null;
                    
                    console.log(`✅ Package ${packagingIndex + 1} carton: ${packaging.recommendedCarton.sku} (${packaging.utilizationPercent}% utilization)`);
                } else {
                    packaging.recommendedCarton = null;
                    packaging.packingError = response.data.error || 'Kein passender Karton gefunden';
                    packaging.utilizationPercent = null;
                    
                    console.warn(`❌ No carton found for package ${packagingIndex + 1}:`, packaging.packingError);
                }
                
            } catch (error) {
                console.error(`Error selecting carton for package ${packagingIndex + 1}:`, error);
                packaging.recommendedCarton = null;
                packaging.packingError = 'Fehler bei Kartonauswahl';
                packaging.utilizationPercent = null;
            } finally {
                packaging.isLoadingCarton = false;
            }
        },

        // Remove article from packaging
        async removeArticleFromPackaging(packagingIndex, article) {
            const packaging = this.packagings[packagingIndex];
            const articleIndex = packaging.articles.findIndex(a => 
                a.sku === article.sku && a.designId === article.designId
            );
            
            if (articleIndex >= 0) {
                packaging.articles.splice(articleIndex, 1);
                
                // Recalculate carton recommendation
                await this.updatePackageCartonRecommendation(packagingIndex);
                
                this.$emit('show-message', {
                    message: `📤 ${article.sku} aus Paket ${packagingIndex + 1} entfernt`,
                    color: 'info'
                });
                
                console.log(`📤 Removed ${article.sku} from package ${packagingIndex + 1}`);
            }
        },

        // Add new packaging
        addNewPackaging() {
            this.packagings.push({
                id: this.nextPackagingId++,
                articles: [],
                weight: null,
                recommendedCarton: null,
                isLoadingCarton: false,
                packingError: null,
                utilizationPercent: null
            });
        },

        // Remove packaging
        removePackaging(index) {
            if (!this.packagings || this.packagings.length <= 2) return;
            
            const packaging = this.packagings[index];
            
            // Move articles back to unassigned state
            if (packaging.articles) {
                packaging.articles.forEach(article => {
                    // For the corrected workflow, we don't need to emit unassigned events
                    // since scanning happens after assignment
                });
            }
            
            this.packagings.splice(index, 1);
        },

        // Validate weight input
        validateWeight(index) {
            const packaging = this.packagings[index];
            
            if (packaging.weight && packaging.weight <= 0) {
                packaging.weight = null;
            }
        },

        // Finalize split and emit result
        async finalizeSplit() {
            if (!this.canCompleteSplit) return;
            
            this.isProcessing = true;
            
            try {
                // Prepare split data with carton recommendations
                const splitData = this.packagings
                    .filter(packaging => packaging.articles && packaging.articles.length > 0)
                    .map(packaging => ({
                        articles: packaging.articles,
                        weight: packaging.weight,
                        recommendedCarton: packaging.recommendedCarton,
                        cartonSku: packaging.recommendedCarton?.sku,
                        utilization: packaging.utilizationPercent
                    }));
                
                console.log('📦 Finalizing split with smart packaging:', splitData);
                
                this.$emit('split-complete', splitData);
                
            } catch (error) {
                console.error('Error finalizing split:', error);
                this.$emit('show-message', {
                    message: '❌ Fehler beim Abschließen des Splits',
                    color: 'error'
                });
            } finally {
                this.isProcessing = false;
            }
        }
    }
}
</script>

<style scoped>
/* Fullscreen Layout */
.fullscreen-card {
    height: 100vh;
    display: flex;
    flex-direction: column;
}

.modal-header {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
    color: white !important;
    border-bottom: 1px solid #0d47a1;
}

.modal-title {
    font-size: 24px !important;
    font-weight: 600 !important;
    color: white !important;
}

.progress-indicator {
    display: flex;
    align-items: center;
}

.fullscreen-content {
    flex: 1;
    height: calc(100vh - 64px - 72px); /* Subtract header and footer */
    overflow: hidden;
}

/* Articles Sidebar */
.articles-sidebar {
    width: 350px;
    background-color: #f8f9fa;
    border-right: 2px solid #dee2e6;
    display: flex;
    flex-direction: column;
}

.sidebar-header {
    padding: 20px;
    background: white;
    border-bottom: 1px solid #dee2e6;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.sidebar-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.sidebar-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
}

/* Articles Grid */
.articles-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

.article-card {
    background: white;
    border: 2px solid #e3f2fd;
    border-radius: 12px;
    padding: 16px;
    position: relative;
    cursor: grab;
    transition: all 0.3s ease;
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.article-card:not(.assigned):hover {
    border-color: #2196f3;
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
    transform: translateY(-2px);
}

.article-card.assigned {
    background-color: #e8f5e8;
    border-color: #4caf50;
    cursor: not-allowed;
    opacity: 0.8;
}

.article-card.dragging {
    opacity: 0.5;
    transform: scale(0.95);
}

.article-content {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    width: 100%;
}

.article-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.article-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
}

.article-main {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.article-sku {
    font-family: 'Roboto Mono', monospace;
    font-weight: 600;
    font-size: 14px;
    color: #333;
}

.article-qty {
    font-size: 12px;
    color: #4caf50;
    background-color: #e8f5e8;
    padding: 4px 8px;
    border-radius: 6px;
    font-weight: 500;
}

.article-meta {
    display: flex;
    align-items: center;
    gap: 8px;
}

.assigned-to {
    font-size: 12px;
    color: #4caf50;
    font-weight: 500;
}

/* Remove old drag handle style since we don't use it anymore */

/* Section Titles */
.section-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
}

/* Main Packages Area */
.packages-main-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 20px;
    overflow-y: auto;
}

.main-area-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e0e0e0;
}

.main-title {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.add-package-btn {
    text-transform: none !important;
    font-weight: 500;
}

/* Packages Grid */
.packages-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 24px;
    flex: 1;
}

.package-card {
    transition: transform 0.2s ease, background 0.2s ease, border 0.2s ease, box-shadow 0.2s ease;
    backface-visibility: hidden; /* Prevent flickering */
}

.package-card.drop-active {
    transform: scale(1.01);
}

.package-card.drop-target {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%);
    border: 3px dashed #2196f3 !important;
    box-shadow: 0 8px 24px rgba(33, 150, 243, 0.3);
    transform: scale(1.01);
}

.package-card.drop-target .packaging-header {
    background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
    color: white;
}

.package-card.drop-target .packaging-header .carton-image-container {
    border-color: white;
    background-color: rgba(255,255,255,0.95);
}

.packaging-card {
    transition: all 0.2s ease;
    min-height: 400px;
    height: 100%;
    backface-visibility: hidden; /* Prevent flickering */
}

/* Packaging Header */
.packaging-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    padding: 16px !important;
}

.header-content {
    flex: 1;
    display: flex;
    align-items: center;
}

/* Packaging Header States */
.no-packaging-state,
.loading-packaging-state,
.selected-packaging-state,
.error-packaging-state {
    display: flex;
    align-items: center;
    width: 100%;
}

/* Carton Image Container */
.carton-image-container {
    width: 52px;
    height: 52px;
    background-color: white;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.carton-img {
    border-radius: 4px;
}

.carton-details {
    flex: 1;
    min-width: 0;
    margin-left: 12px;
}

.carton-name {
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
    color: #333;
    margin-bottom: 2px;
}

.carton-specs {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.carton-sku {
    font-family: 'Roboto Mono', monospace;
    font-size: 13px;
    font-weight: 600;
    color: #666;
    background-color: #f5f5f5;
    padding: 4px 8px;
    border-radius: 6px;
}

/* Drop Zones */
.empty-drop-zone {
    text-align: center;
    padding: 40px 20px;
    border: 3px dashed #e0e0e0;
    border-radius: 12px;
    background-color: #fafafa;
    transition: all 0.3s ease;
    min-height: 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.assigned-articles.drop-zone {
    border-radius: 8px;
    min-height: 120px;
    transition: all 0.3s ease;
}

.assigned-articles {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.assigned-article {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #4caf50;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: all 0.2s ease;
    cursor: grab;
    position: relative;
}

.assigned-article:hover {
    background-color: #f0f8ff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.assigned-article:active {
    cursor: grabbing;
}

.assigned-article.dragging {
    opacity: 0.5;
    transform: scale(0.95);
}

.drag-handle-assigned {
    flex-shrink: 0;
    opacity: 0.6;
    transition: opacity 0.2s ease;
}

.assigned-article:hover .drag-handle-assigned {
    opacity: 1;
}

/* Assigned Article Info */
.assigned-article .article-info {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-width: 0;
}

.article-sku {
    font-family: 'Roboto Mono', monospace;
    font-weight: 600;
    color: #333;
    font-size: 14px;
    flex: 1;
}

.article-qty {
    background-color: #4caf50;
    color: white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
}

.scan-status {
    margin-left: 8px;
}

.articles-section {
    margin-top: 16px;
    margin-bottom: 8px;
}

/* Weight Section */
.weight-section {
    background-color: #fff3e0;
    padding: 16px;
    border-radius: 12px;
    border: 2px solid #ffb300;
    margin-top: 4px;
}

.weight-input {
    margin-bottom: 0 !important;
}

.weight-input .v-input__control {
    margin-bottom: 0 !important;
}

.weight-input .v-text-field__details {
    display: none !important;
}

/* Drop Target Effects */
.package-card.drop-target .empty-drop-zone {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%);
    border-color: #2196f3;
    border-width: 3px;
    animation: dropPulse 1s ease-in-out infinite;
}

.package-card.drop-target .assigned-articles {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%);
    border-color: #2196f3;
    border-width: 3px;
}

/* Stabilize transitions to prevent flickering */
.package-card {
    transform-origin: center;
    will-change: transform, background, border;
}

.package-card.drop-target {
    transform-origin: center;
}

@keyframes dropPulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.02); }
    100% { transform: scale(1); }
}

/* Drop Hint Animation */
.drop-hint {
    margin-top: 8px;
    animation: bounce 1s ease-in-out infinite;
}

@keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-5px); }
    60% { transform: translateY(-3px); }
}

/* Inter-package Move Indicator */
.inter-package-drop-indicator {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border: 2px solid #2196f3;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: pulse 1.5s ease-in-out infinite;
}

.inter-package-drop-indicator span {
    font-weight: 500;
    color: #1976d2;
    font-size: 14px;
}

@keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.02); }
    100% { transform: scale(1); }
}

/* Split Summary */
.split-summary {
    display: flex;
    gap: 16px;
    align-items: center;
}

.summary-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #666;
    font-weight: 500;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .articles-sidebar {
        width: 300px;
    }
    
    .packages-grid {
        grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
        gap: 16px;
    }
}

@media (max-width: 900px) {
    .fullscreen-content {
        flex-direction: column;
    }
    
    .articles-sidebar {
        width: 100%;
        max-height: 300px;
        border-right: none;
        border-bottom: 2px solid #dee2e6;
    }
    
    .packages-grid {
        grid-template-columns: 1fr;
    }
}

.v-toolbar {
    flex: none !important;
}
</style>